import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequiredCurrentUserClientId } from "../../../core/auth/tenant";
import {
  createUser,
  deleteUser,
  getUsers,
  updateUser,
} from "../services/usersService";

const VALID_TIMEZONES = ["PKT", "GMT", "UTC", "EST", "CST", "MST", "PST"];

const mapSmsNotification = (val) => {
  if (val === true || val === "true") return "Enabled";
  return "Disabled";
};

const toUIUser = (apiUser) => {
  const timezone =
    apiUser.timezone && VALID_TIMEZONES.includes(apiUser.timezone)
      ? apiUser.timezone
      : "UTC";
  const rawVoicemailId = Number(apiUser.voicemail_number_id);
  const voicemailNumberId =
    Number.isFinite(rawVoicemailId) && rawVoicemailId > 0 ? rawVoicemailId : null;

  return {
    id: apiUser.id,
    client_id: apiUser.client_id ?? null,
    firstName: apiUser.firstname || "",
    lastName: apiUser.lastname || "",
    toAddress: apiUser.pbx_email || "",
    deliveryAddress: apiUser.email || "",
    emailMode: apiUser.user_status === "enabled" ? "Enabled" : "Disabled",
    smsMode: mapSmsNotification(apiUser.sms_notification),
    mobileNumber: apiUser.phone || "",
    voicemailNumber: "",
    voicemailNumberId,
    timeZone: timezone,
    userRole: apiUser.user_role || "",
    userType: apiUser.user_type || "",
    twoFaEnabled: apiUser.two_fa_enabled ?? false,
    createdAt: apiUser.created_at || "",
    updatedAt: apiUser.updated_at || "",
  };
};

const toAPIUser = (formData, existingUser = {}) => {
  const requestedTimezone = formData.timeZone;
  const timezone =
    requestedTimezone && VALID_TIMEZONES.includes(requestedTimezone)
      ? requestedTimezone
      : "UTC";

  const body = {
    firstname: formData.firstName,
    lastname: formData.lastName,
    email: formData.deliveryAddress,
    phone: formData.mobileNumber || undefined,
    sms_notification: formData.smsMode !== "Disabled",
    timezone,
    user_status: formData.emailMode === "Enabled" ? "enabled" : "disabled",
  };

  if (!existingUser.userType || existingUser.userType === "console") {
    body.user_role = formData.userRole || existingUser.userRole || "viewer";
    body.user_type = "console";
  }

  if (formData.password) {
    body.password = formData.password;
  }

  return body;
};

const toAPIUserUpdate = (formData, existingUser = {}) => {
  const requestedTimezone = formData.timeZone || existingUser.timeZone;
  const timezone =
    requestedTimezone && VALID_TIMEZONES.includes(requestedTimezone)
      ? requestedTimezone
      : "UTC";

  const resolvedStatus = formData.userStatus || existingUser.emailMode || "Enabled";

  const body = {
    firstname: formData.firstName,
    lastname: formData.lastName,
    phone: formData.mobileNumber || undefined,
    sms_notification: formData.smsMode !== "Disabled",
    timezone,
    user_status: resolvedStatus === "Enabled" ? "enabled" : "disabled",
    two_fa_enabled: existingUser.twoFaEnabled ?? true,
  };

  if (!existingUser.userType || existingUser.userType === "console") {
    body.user_role = existingUser.userRole || "viewer";
  }

  return body;
};

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  if (error?.message) return error.message;
  return fallback;
};

export const fetchUsersThunk = createAsyncThunk(
  "users/fetchUsers",
  async (_, { getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    const data = await getUsers(clientId, { limit: 100, offset: 0 });
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to load users.");
    }

    const list = Array.isArray(data?.users)
      ? data.users
      : Array.isArray(data)
        ? data
        : [];

    return {
      users: list.map(toUIUser),
      total: data?.total ?? list.length,
    };
  },
);

export const addUserThunk = createAsyncThunk(
  "users/addUser",
  async (formData, { dispatch, getState, rejectWithValue }) => {
    let clientId;
    try {
      clientId = getRequiredCurrentUserClientId(getState());
    } catch (error) {
      return rejectWithValue(error.message);
    }

    const body = {
      ...toAPIUser(formData),
      client_id: clientId,
      user_type: "console",
      user_role: formData.userRole || "viewer",
    };

    const data = await createUser(body);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to add user.");
    }
    const createdUserId = data?.id ?? data?.user?.id ?? null;

    await dispatch(fetchUsersThunk());
    return { success: true, userId: createdUserId };
  },
);

export const updateUserThunk = createAsyncThunk(
  "users/updateUser",
  async ({ id, formData, existingUser }, { dispatch, rejectWithValue }) => {
    const body = toAPIUserUpdate(formData, existingUser);
    const data = await updateUser(id, body);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to update user.");
    }

    await dispatch(fetchUsersThunk());
    return { success: true };
  },
);

export const deleteUserThunk = createAsyncThunk(
  "users/deleteUser",
  async (id, { rejectWithValue }) => {
    const data = await deleteUser(id);
    if (data?.error) {
      return rejectWithValue(data.error.message || "Unable to delete user.");
    }

    return { id };
  },
);

export const bulkDeleteUsersThunk = createAsyncThunk(
  "users/bulkDeleteUsers",
  async (ids, { dispatch, rejectWithValue }) => {
    const results = await Promise.all(ids.map((id) => deleteUser(id)));
    const failed = results.filter((result) => result?.error);

    await dispatch(fetchUsersThunk());

    if (failed.length > 0) {
      return rejectWithValue(`Failed to delete ${failed.length} user(s).`);
    }

    return { success: true };
  },
);

export const bulkStatusChangeThunk = createAsyncThunk(
  "users/bulkStatusChange",
  async ({ ids, status }, { dispatch, getState, rejectWithValue }) => {
    const users = getState().users.users;

    const results = await Promise.all(
      ids.map((id) => {
        const user = users.find((item) => item.id === id);
        if (!user) return Promise.resolve({});

        const timezone =
          user.timeZone && VALID_TIMEZONES.includes(user.timeZone)
            ? user.timeZone
            : "UTC";

        const payload = {
          firstname: user.firstName,
          lastname: user.lastName,
          user_status: status,
          sms_notification: user.smsMode !== "Disabled",
          timezone,
          phone: user.mobileNumber || undefined,
          two_fa_enabled: user.twoFaEnabled,
        };

        if (!user.userType || user.userType === "console") {
          payload.user_role = user.userRole || "viewer";
        }

        return updateUser(id, payload);
      }),
    );

    const failed = results.filter((result) => result?.error);

    await dispatch(fetchUsersThunk());

    if (failed.length > 0) {
      return rejectWithValue(`Failed to update ${failed.length} user(s).`);
    }

    return { success: true };
  },
);

const initialState = {
  users: [],
  isLoading: false,
  isProcessing: false,
  error: null,
  total: 0,
};

const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    clearUsersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsersThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsersThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users;
        state.total = action.payload.total;
      })
      .addCase(fetchUsersThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = getErrorMessage(action.payload, "Unable to load users.");
      })
      .addCase(addUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(addUserThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(addUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to add user.");
      })
      .addCase(updateUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(updateUserThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(updateUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to update user.");
      })
      .addCase(deleteUserThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(deleteUserThunk.fulfilled, (state, action) => {
        state.isProcessing = false;
        state.users = state.users.filter((user) => user.id !== action.payload.id);
        state.total = Math.max(state.total - 1, 0);
      })
      .addCase(deleteUserThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to delete user.");
      })
      .addCase(bulkDeleteUsersThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(bulkDeleteUsersThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(bulkDeleteUsersThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to delete selected users.");
      })
      .addCase(bulkStatusChangeThunk.pending, (state) => {
        state.isProcessing = true;
        state.error = null;
      })
      .addCase(bulkStatusChangeThunk.fulfilled, (state) => {
        state.isProcessing = false;
      })
      .addCase(bulkStatusChangeThunk.rejected, (state, action) => {
        state.isProcessing = false;
        state.error = getErrorMessage(action.payload, "Unable to update selected users.");
      });
  },
});

export const { clearUsersError } = usersSlice.actions;

export const selectUsers = (state) => state.users.users;
export const selectUsersLoading = (state) => state.users.isLoading;
export const selectUsersProcessing = (state) => state.users.isProcessing;
export const selectUsersError = (state) => state.users.error;
export const selectUsersTotal = (state) => state.users.total;

export default usersSlice.reducer;
